import { useState } from 'react';
import { Send, Plus, X, Clock, DollarSign, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { Job, ApplicationFormData, BroUser } from '../types';

interface ApplicationFormProps {
  job: Job;
  user?: BroUser;
  onSubmit: (applicationData: ApplicationFormData) => void;
  onCancel: () => void;
}

const ApplicationForm = ({ job, user, onSubmit, onCancel }: ApplicationFormProps) => {
  const [formData, setFormData] = useState<ApplicationFormData>({
    cover_letter: '',
    proposed_budget: job.budget_max || undefined,
    proposed_timeline: '',
    portfolio_links: [],
    relevant_experience: ''
  });

  const [newPortfolioLink, setNewPortfolioLink] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.cover_letter.trim()) {
      newErrors.cover_letter = 'Cover letter is required';
    } else if (formData.cover_letter.length < 50) {
      newErrors.cover_letter = 'Cover letter must be at least 50 characters';
    }

    if (formData.proposed_budget && formData.proposed_budget <= 0) {
      newErrors.proposed_budget = 'Budget must be greater than 0';
    }

    if (job.budget_max && formData.proposed_budget && formData.proposed_budget > job.budget_max) {
      newErrors.proposed_budget = `Budget cannot exceed ${job.budget_max} ${job.budget_currency}`;
    }

    if (!formData.proposed_timeline?.trim()) {
      newErrors.proposed_timeline = 'Proposed timeline is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      onSubmit(formData);
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addPortfolioLink = () => {
    if (newPortfolioLink.trim() && !formData.portfolio_links.includes(newPortfolioLink.trim())) {
      setFormData(prev => ({
        ...prev,
        portfolio_links: [...prev.portfolio_links, newPortfolioLink.trim()]
      }));
      setNewPortfolioLink('');
    }
  };

  const removePortfolioLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      portfolio_links: prev.portfolio_links.filter((_, i) => i !== index)
    }));
  };

  const formatBudget = (min?: number, max?: number, currency?: string) => {
    const currencySymbol = currency === 'BRO_SCORE' ? 'BroScore' : currency === 'TOP1_TOKEN' ? '$TOP1' : '$';
    
    if (min && max) {
      return `${min}-${max} ${currencySymbol}`;
    }
    if (max) {
      return `Up to ${max} ${currencySymbol}`;
    }
    return 'Budget negotiable';
  };

  return (
    <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
      <div className="mb-6">
        <h3 className="text-xl font-bold text-tan mb-2">Apply for "{job.title}"</h3>
        <div className="text-tan-dark text-sm mb-3">
          Budget: {formatBudget(job.budget_min, job.budget_max, job.budget_currency)}
        </div>
        
        {/* Job Requirements Check */}
        <div className="bg-navy rounded-lg p-3 mb-4">
          <h4 className="text-tan font-medium mb-2">Requirements Check:</h4>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <span className={user && user.bro_score >= job.min_bro_score ? 'text-green-400' : 'text-red-400'}>
                {user && user.bro_score >= job.min_bro_score ? '✓' : '✗'}
              </span>
              <span className="text-tan-dark">
                BroScore: {user?.bro_score || 0} / {job.min_bro_score} required
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={!job.requires_verification || user?.telegram_verified ? 'text-green-400' : 'text-red-400'}>
                {!job.requires_verification || user?.telegram_verified ? '✓' : '✗'}
              </span>
              <span className="text-tan-dark">
                Telegram verification {job.requires_verification ? 'required' : 'not required'}
              </span>
            </div>
          </div>
        </div>

        {/* Warning if requirements not met */}
        {user && (user.bro_score < job.min_bro_score || (job.requires_verification && !user.telegram_verified)) && (
          <Alert className="mb-4">
            <AlertDescription>
              You don't meet all the requirements for this job. Your application may be automatically rejected.
            </AlertDescription>
          </Alert>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Cover Letter */}
        <div>
          <Label htmlFor="cover_letter" className="text-tan font-medium">
            Cover Letter *
          </Label>
          <Textarea
            id="cover_letter"
            placeholder="Explain why you're the perfect fit for this job. Highlight your relevant experience and how you plan to approach this project..."
            value={formData.cover_letter}
            onChange={(e) => setFormData(prev => ({ ...prev, cover_letter: e.target.value }))}
            rows={6}
            className={`mt-1 bg-navy border-tan-dark text-tan placeholder-tan-dark ${
              errors.cover_letter ? 'border-red-500' : ''
            }`}
          />
          {errors.cover_letter && (
            <p className="text-red-400 text-xs mt-1">{errors.cover_letter}</p>
          )}
          <p className="text-tan-dark text-xs mt-1">
            {formData.cover_letter.length}/500 characters (minimum 50)
          </p>
        </div>

        {/* Proposed Budget */}
        <div>
          <Label htmlFor="proposed_budget" className="text-tan font-medium">
            Proposed Budget ({job.budget_currency})
          </Label>
          <div className="relative mt-1">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
            <Input
              id="proposed_budget"
              type="number"
              placeholder={job.budget_max ? `Max: ${job.budget_max}` : 'Enter your rate'}
              value={formData.proposed_budget || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                proposed_budget: e.target.value ? Number(e.target.value) : undefined 
              }))}
              className={`pl-10 bg-navy border-tan-dark text-tan placeholder-tan-dark ${
                errors.proposed_budget ? 'border-red-500' : ''
              }`}
            />
          </div>
          {errors.proposed_budget && (
            <p className="text-red-400 text-xs mt-1">{errors.proposed_budget}</p>
          )}
          <p className="text-tan-dark text-xs mt-1">
            Your proposed rate for this project
          </p>
        </div>

        {/* Proposed Timeline */}
        <div>
          <Label htmlFor="proposed_timeline" className="text-tan font-medium">
            Proposed Timeline *
          </Label>
          <div className="relative mt-1">
            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
            <Input
              id="proposed_timeline"
              placeholder="e.g., 2 weeks, 5 days, 1 month"
              value={formData.proposed_timeline}
              onChange={(e) => setFormData(prev => ({ ...prev, proposed_timeline: e.target.value }))}
              className={`pl-10 bg-navy border-tan-dark text-tan placeholder-tan-dark ${
                errors.proposed_timeline ? 'border-red-500' : ''
              }`}
            />
          </div>
          {errors.proposed_timeline && (
            <p className="text-red-400 text-xs mt-1">{errors.proposed_timeline}</p>
          )}
          <p className="text-tan-dark text-xs mt-1">
            How long will it take you to complete this project?
          </p>
        </div>

        {/* Portfolio Links */}
        <div>
          <Label className="text-tan font-medium">Portfolio Links</Label>
          <div className="mt-2 space-y-2">
            {/* Add new link */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
                <Input
                  placeholder="https://github.com/username/project or portfolio URL"
                  value={newPortfolioLink}
                  onChange={(e) => setNewPortfolioLink(e.target.value)}
                  className="pl-10 bg-navy border-tan-dark text-tan placeholder-tan-dark"
                />
              </div>
              <Button
                type="button"
                onClick={addPortfolioLink}
                variant="outline"
                className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
              >
                <Plus size={16} />
              </Button>
            </div>

            {/* Existing links */}
            {formData.portfolio_links.map((link, index) => (
              <div key={index} className="flex items-center justify-between bg-navy rounded p-2 border border-tan-dark">
                <span className="text-tan text-sm truncate flex-1">{link}</span>
                <Button
                  type="button"
                  onClick={() => removePortfolioLink(index)}
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300"
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-tan-dark text-xs mt-1">
            Share links to your relevant work, GitHub repositories, or portfolio
          </p>
        </div>

        {/* Relevant Experience */}
        <div>
          <Label htmlFor="relevant_experience" className="text-tan font-medium">
            Relevant Experience
          </Label>
          <Textarea
            id="relevant_experience"
            placeholder="Describe your relevant experience for this project. Mention similar projects you've completed, technologies you've worked with, etc."
            value={formData.relevant_experience}
            onChange={(e) => setFormData(prev => ({ ...prev, relevant_experience: e.target.value }))}
            rows={4}
            className="mt-1 bg-navy border-tan-dark text-tan placeholder-tan-dark"
          />
          <p className="text-tan-dark text-xs mt-1">
            Help the employer understand why you're qualified (optional)
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-tan text-navy hover:bg-tan-dark flex-1"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-navy mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Submit Application
              </>
            )}
          </Button>
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;